import type { PublicNotificationDto } from '~~/shared/types/notification'

export function useNotificationSocket() {
  const connected = useState('morning-notification-socket-connected', () => false)
  const socket = useState<WebSocket | null>('morning-notification-socket', () => null)
  let retryTimer: ReturnType<typeof setTimeout> | undefined
  let attempt = 0
  let stopped = false
  const notifications = useNotifications()
  const auth = useAuth()
  function schedule() {
    if (stopped || retryTimer || !auth.authenticated.value) return
    retryTimer = setTimeout(
      () => {
        retryTimer = undefined
        connect()
      },
      Math.min(30_000, 1000 * 2 ** Math.min(attempt++, 5)),
    )
  }
  function connect() {
    if (
      import.meta.server ||
      stopped ||
      !auth.authenticated.value ||
      socket.value?.readyState === WebSocket.OPEN ||
      socket.value?.readyState === WebSocket.CONNECTING
    )
      return
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const current = new WebSocket(`${protocol}//${location.host}/api/notifications/socket`)
    socket.value = current
    current.onopen = async () => {
      attempt = 0
      connected.value = true
      await notifications.refresh(20)
    }
    current.onmessage = (event) => {
      try {
        const message = JSON.parse(String(event.data)) as {
          type: string
          notification?: PublicNotificationDto
        }
        if (message.type === 'notification.created' && message.notification)
          notifications.receive(message.notification)
        else if (message.type !== 'notification.ready') void notifications.refresh(20)
      } catch {
        /* message invalide */
      }
    }
    current.onclose = () => {
      connected.value = false
      if (socket.value === current) socket.value = null
      schedule()
    }
    current.onerror = () => current.close()
  }
  function resync() {
    if (auth.authenticated.value) void notifications.refresh(20)
    connect()
  }
  function start() {
    stopped = false
    connect()
    window.addEventListener('online', connect)
    window.addEventListener('focus', resync)
  }
  function stop() {
    stopped = true
    if (retryTimer) clearTimeout(retryTimer)
    retryTimer = undefined
    window.removeEventListener('online', connect)
    window.removeEventListener('focus', resync)
    socket.value?.close()
    socket.value = null
    connected.value = false
  }
  watch(
    () => auth.authenticated.value,
    (value) => (value ? start() : stop()),
  )
  return { connected, start, stop }
}
